import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Download, Copy } from 'lucide-react';
import QRCode from 'react-qr-code';
import TopBar from '../components/common/TopBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { kioskService } from '../services/api';
import { toast } from 'sonner';

const KioskQRCode = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kiosk, setKiosk] = useState(null);
  const [loading, setLoading] = useState(true);

  // 2026-06-29: envolvido com useCallback pra satisfazer ESLint react-hooks/exhaustive-deps
  const loadKiosk = useCallback(async () => {
    try {
      const data = await kioskService.getById(id);
      setKiosk(data);
    } catch (e) {
      toast.error('Erro ao carregar estabelecimento');
    } finally {
      setLoading(false);
    }
  // 2026-06-29: id é dependência — recarrega quando muda
  }, [id]);

  // 2026-06-29: loadKiosk agora é useCallback — pode ir no dep array
  useEffect(() => {
    loadKiosk();
  }, [loadKiosk]);

  const kioskUrl = `${window.location.origin}/kiosk/${id}?utm_source=qrcode&utm_medium=share`;

  const handleShare = async () => {
    const shareData = {
      title: `${kiosk?.display_name} - Beach Pay`,
      text: `🏖️ Peça direto da areia com o Beach Pay! Acesse o cardápio do ${kiosk?.display_name}`,
      url: kioskUrl,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Compartilhado!');
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(kioskUrl);
        toast.success('Link copiado!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Fallback to clipboard
        try {
          await navigator.clipboard.writeText(kioskUrl);
          toast.success('Link copiado!');
        } catch {
          toast.error('Não foi possível compartilhar');
        }
      }
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(kioskUrl);
    toast.success('Link copiado!');
  };

  const handleDownload = () => {
    // Get the SVG element
    const svg = document.querySelector('[data-testid="qrcode-svg"] svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 800;
      canvas.height = 800;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 100, 100, 600, 600);
      
      const link = document.createElement('a');
      link.download = `beach-pay-${kiosk?.qr_code || id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR Code baixado!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) return <LoadingSpinner size="lg" text="Carregando..." />;
  if (!kiosk) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="qr-code-page">
      <TopBar title="QR Code" showBack />

      <div className="max-w-md mx-auto p-4">
        {/* Card */}
        <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white text-center shadow-lg mb-6">
          <p className="text-white/80 text-xs uppercase tracking-wider font-bold">Beach Pay</p>
          <h1 className="font-['Outfit'] text-2xl font-bold mt-1">{kiosk.display_name}</h1>
          <p className="text-white/90 text-sm mt-1">Aponte a câmera para pedir</p>
        </div>

        {/* QR Code */}
        <div 
          data-testid="qrcode-svg"
          className="bg-white rounded-2xl p-8 shadow-lg flex justify-center items-center mb-6 border-2 border-border"
        >
          <QRCode 
            value={kioskUrl} 
            size={256}
            level="M"
            fgColor="#1A1A1A"
            bgColor="#FFFFFF"
          />
        </div>

        {/* Code */}
        <div className="bg-muted rounded-xl p-3 text-center mb-6">
          <p className="text-xs text-muted-foreground">Código do estabelecimento</p>
          <p className="font-mono font-bold text-lg mt-1">{kiosk.qr_code}</p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            data-testid="share-qr-button"
            onClick={handleShare}
            className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-95 shadow-lg"
          >
            <Share2 className="w-5 h-5" />
            Compartilhar com amigos
          </button>

          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="copy-link"
              onClick={handleCopy}
              className="py-2.5 bg-white border border-border rounded-full font-semibold text-sm flex items-center justify-center gap-2 hover:bg-muted transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copiar link
            </button>
            <button
              data-testid="download-qr"
              onClick={handleDownload}
              className="py-2.5 bg-white border border-border rounded-full font-semibold text-sm flex items-center justify-center gap-2 hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar QR
            </button>
          </div>
        </div>

        <div className="mt-6 bg-secondary/10 border border-secondary/20 rounded-xl p-4">
          <p className="text-sm font-semibold text-foreground mb-1">
            💡 Compartilhe e ganhe!
          </p>
          <p className="text-xs text-muted-foreground">
            Indique amigos para o Beach Pay e participe de promoções exclusivas. Em breve sistema de cashback por indicação!
          </p>
        </div>
      </div>
    </div>
  );
};

export default KioskQRCode;
